import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { eventsApi, phasesApi, speakersApi, timerApi } from '../lib/api';
import { useSocket } from '../hooks/useSocket';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { formatTime, getPhaseTypeLabel, getPhaseTypeColor } from '../lib/utils';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ArrowLeft, Clock, User, ChevronRight } from 'lucide-react';

export default function PresenterViewPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  
  const [event, setEvent] = useState(null);
  const [phases, setPhases] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timerRunning, setTimerRunning] = useState(false);
  const [phaseElapsed, setPhaseElapsed] = useState(0);

  // Socket handlers
  const socketHandlers = {
    onEventUpdated: (data) => {
      setEvent(data);
      setTimerRunning(data.timer_running);
    },
    onPhaseUpdated: (phase) => {
      setPhases(prev => prev.map(p => p.id === phase.id ? phase : p));
    },
    onTimerAction: ({ event: ev, phases: ph }) => {
      setEvent(ev);
      setPhases(ph);
      setTimerRunning(ev.timer_running);
    },
  };

  useSocket(eventId, socketHandlers);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventRes, phasesRes, speakersRes] = await Promise.all([
          eventsApi.getOne(eventId),
          phasesApi.getAll(eventId),
          speakersApi.getAll(eventId),
        ]);
        setEvent(eventRes.data);
        setPhases(phasesRes.data);
        setSpeakers(speakersRes.data);
        setTimerRunning(eventRes.data.timer_running);
      } catch (error) {
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [eventId, navigate]);

  // Timer interval
  useEffect(() => {
    let interval;
    if (timerRunning) {
      interval = setInterval(() => {
        setPhaseElapsed(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timerRunning]);

  // Reset phase elapsed when current phase changes
  useEffect(() => {
    if (event?.current_phase_id) {
      const currentPhase = phases.find(p => p.id === event.current_phase_id);
      if (currentPhase) {
        setPhaseElapsed(currentPhase.actual_duration || 0);
      }
    }
  }, [event?.current_phase_id, phases]);

  // Keyboard shortcuts (read-only, just for navigation)
  useKeyboardShortcuts([
    { key: 'Escape', action: () => navigate(`/event/${eventId}`) },
  ]);

  // Current and next phase
  const currentPhase = phases.find(p => p.id === event?.current_phase_id);
  const currentPhaseIndex = phases.findIndex(p => p.id === event?.current_phase_id);
  const nextPhase = currentPhaseIndex >= 0 ? phases[currentPhaseIndex + 1] : phases[0];
  
  const currentSpeaker = currentPhase ? speakers.find(s => s.id === currentPhase.speaker_id) : null;
  const nextSpeaker = nextPhase ? speakers.find(s => s.id === nextPhase.speaker_id) : null;

  // Time calculations
  const remaining = currentPhase ? currentPhase.duration_seconds - phaseElapsed : 0;
  const isOvertime = remaining < 0;
  const isWarning = remaining > 0 && remaining <= 60; // Last minute warning

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#09090B]">
        <div className="animate-spin w-8 h-8 border-2 border-white border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] text-white overflow-hidden" data-testid="presenter-view">
      {/* Subtle scanlines overlay */}
      <div className="fixed inset-0 scanlines opacity-20 pointer-events-none" />
      
      {/* Header - minimal */}
      <div className="fixed top-0 left-0 right-0 z-50 px-8 py-4 flex items-center justify-between bg-gradient-to-b from-black/80 to-transparent">
        <Button 
          variant="ghost" 
          onClick={() => navigate(`/event/${eventId}`)}
          className="text-white/50 hover:text-white"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Volver
        </Button>
        <div className="text-sm text-white/40 font-mono">{event?.name}</div>
        <div className="flex items-center gap-2">
          {timerRunning && (
            <Badge className="bg-red-500 text-white animate-pulse">EN VIVO</Badge>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="h-screen flex flex-col items-center justify-center px-8">
        {currentPhase ? (
          <>
            {/* Current phase info */}
            <div className="text-center mb-8">
              <Badge 
                className="text-sm px-4 py-1 mb-4"
                style={{ backgroundColor: getPhaseTypeColor(currentPhase.phase_type) }}
              >
                {getPhaseTypeLabel(currentPhase.phase_type)}
              </Badge>
              <h1 className="font-heading text-5xl md:text-7xl font-black tracking-tight mb-4">
                {currentPhase.name}
              </h1>
              {currentSpeaker && (
                <div className="flex items-center justify-center gap-3 text-xl text-white/70">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center font-medium">
                    {currentSpeaker.initials}
                  </div>
                  <span>{currentSpeaker.name}</span>
                  {currentSpeaker.role && (
                    <span className="text-white/40">· {currentSpeaker.role}</span>
                  )}
                </div>
              )}
            </div>

            {/* Main timer */}
            <div className={`relative mb-12 ${isOvertime ? 'timer-glow-red' : isWarning ? 'animate-pulse' : ''}`}>
              <div className={`font-mono text-[14rem] md:text-[18rem] font-bold leading-none tracking-tighter tabular-nums ${
                isOvertime ? 'text-red-500' : isWarning ? 'text-yellow-400' : 'text-white'
              }`}>
                {isOvertime && <span className="text-[8rem] align-top">+</span>}
                {formatTime(Math.abs(remaining), true)}
              </div>
              
              {/* Progress bar */}
              <div className="absolute -bottom-4 left-0 right-0 h-2 bg-white/10 rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all duration-1000 ${
                    isOvertime ? 'bg-red-500' : isWarning ? 'bg-yellow-400' : 'bg-green-500'
                  }`}
                  style={{ width: `${Math.min(100, (phaseElapsed / currentPhase.duration_seconds) * 100)}%` }}
                />
              </div>
            </div>

            {/* Notes if any */}
            {currentPhase.notes && (
              <div className="max-w-2xl text-center mb-12 p-6 rounded-xl bg-white/5 border border-white/10">
                <p className="text-lg text-white/70">{currentPhase.notes}</p>
              </div>
            )}
          </>
        ) : (
          <div className="text-center">
            <Clock className="w-24 h-24 text-white/20 mx-auto mb-6" />
            <h1 className="font-heading text-4xl font-bold text-white/40 mb-2">
              Esperando inicio
            </h1>
            <p className="text-white/30">El operador iniciará el evento en breve</p>
          </div>
        )}
      </div>

      {/* Next phase preview - bottom bar */}
      {nextPhase && nextPhase.id !== currentPhase?.id && (
        <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/90 to-transparent">
          <div className="max-w-4xl mx-auto px-8 py-6">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2 text-white/40">
                <ChevronRight className="w-5 h-5" />
                <span className="text-sm uppercase tracking-wider">Siguiente</span>
              </div>
              
              <div className="flex-1 flex items-center gap-4">
                <Badge 
                  variant="outline"
                  className="border-white/20 text-white/60"
                  style={{ borderColor: getPhaseTypeColor(nextPhase.phase_type) + '60' }}
                >
                  {getPhaseTypeLabel(nextPhase.phase_type)}
                </Badge>
                <span className="font-heading text-xl font-semibold">{nextPhase.name}</span>
                {nextSpeaker && (
                  <span className="text-white/40">· {nextSpeaker.name}</span>
                )}
              </div>
              
              <div className="flex items-center gap-2 text-white/40 font-mono">
                <Clock className="w-4 h-4" />
                {formatTime(nextPhase.duration_seconds)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Keyboard hint */}
      <div className="fixed bottom-4 right-4 text-xs text-white/20">
        <kbd className="kbd bg-white/5 border-white/10">ESC</kbd> para volver
      </div>
    </div>
  );
}
